import { useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, ArrowRight, Clock, MapPin, Sparkles, CalendarRange } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { destinations } from '../data/destinations';
import { Reveal } from '../components/Reveal';

const DestinationDetail = () => {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const destination = destinations.find((d) => d.slug === slug);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  if (!destination) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-ivory px-5" data-testid="destination-not-found">
        <p className="font-heading text-3xl text-navy">{t.destinationsPage.notFound}</p>
        <Link to="/destinations" className="mt-6 text-sm uppercase tracking-[0.2em] text-gold-dark" data-testid="back-to-destinations">
          {t.destinationsPage.back}
        </Link>
      </div>
    );
  }

  const d = destination;

  return (
    <div className="bg-ivory" data-testid={`destination-detail-${d.slug}`}>
      {/* Hero */}
      <section ref={heroRef} className="relative flex h-[62vh] min-h-[26rem] items-end overflow-hidden bg-navy">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={d.image} alt={d.name[lang]} className="h-full w-full scale-105 object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/25 to-navy-dark/30" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-14 md:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/destinations" className="mb-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-ivory/70 transition-colors hover:text-gold" data-testid="back-to-destinations">
              <ArrowLeft className="h-4 w-4" /> {t.destinationsPage.back}
            </Link>
            <h1 className="font-heading text-5xl font-medium tracking-tight text-ivory md:text-7xl" data-testid="destination-title">{d.name[lang]}</h1>
            <p className="mt-4 flex items-center gap-2 text-sm text-gold-light">
              <Clock className="h-4 w-4" /> {t.destinationsPage.durationLabel}: {d.duration[lang]}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
        <div className="grid gap-16 lg:grid-cols-5">
          {/* Left: overview + itinerary */}
          <div className="lg:col-span-3">
            <Reveal>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">{t.destinationsPage.overview}</h2>
              <p className="mt-5 text-lg leading-relaxed text-navy/80" data-testid="destination-overview">{d.overview[lang]}</p>
            </Reveal>

            <Reveal delay={0.1} className="mt-16">
              <h2 className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark">
                <CalendarRange className="h-4 w-4" /> {t.destinationsPage.itinerary}
              </h2>
              <ol className="mt-8 border-l border-gold/50" data-testid="destination-itinerary">
                {d.itinerary.map((item, i) => (
                  <li key={i} className="relative py-5 pl-10">
                    <span className="absolute -left-[7px] top-6 h-3.5 w-3.5 rotate-45 border border-gold bg-ivory" aria-hidden="true" />
                    <p className="text-sm leading-relaxed text-navy/75">{item[lang]}</p>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>

          {/* Right: attractions + experiences */}
          <div className="lg:col-span-2">
            <Reveal delay={0.05}>
              <div className="bg-beige p-8 md:p-10" data-testid="destination-attractions">
                <h3 className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold-dark">
                  <MapPin className="h-4 w-4" /> {t.destinationsPage.attractions}
                </h3>
                <ul className="mt-6 flex flex-col gap-4">
                  {d.attractions.map((a, i) => (
                    <li key={i} className="border-b border-navy/10 pb-4 text-sm leading-relaxed text-navy/80">{a[lang]}</li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.12} className="mt-6">
              <div className="bg-navy p-8 text-ivory md:p-10" data-testid="destination-experiences">
                <h3 className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">
                  <Sparkles className="h-4 w-4" /> {t.destinationsPage.experiences}
                </h3>
                <ul className="mt-6 flex flex-col gap-4">
                  {d.experiences.map((e, i) => (
                    <li key={i} className="border-b border-ivory/10 pb-4 text-sm leading-relaxed text-ivory/75">{e[lang]}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal className="mt-20 border-t border-navy/10 pt-14 text-center">
          <Link
            to={`/plan?destination=${d.slug}`}
            data-testid="customize-trip-button"
            className="group inline-flex items-center gap-3 bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-gold-light"
          >
            {t.destinationsPage.customize}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default DestinationDetail;
